import Decimal from 'decimal.js';
import { useState, useRef } from 'preact/hooks';
import { useAppState, buildTFSAState, type WizardInputs } from '../lib/state';
import { availableRoom, cumulativeRoom } from '../engine/tfsa';
import { formatCAD } from '../lib/decimal';
import { localIsoNow } from '../lib/timezone';
import type { ResidencyYear } from '../engine/types';
import { Modal } from '../components/Modal';
import { ButtonPrimary } from '../components/ButtonPrimary';
import { ProgressDots } from '../components/ProgressDots';

type Screen =
  | 'disclaimer'
  | 'welcome'
  | 'step1'
  | 'step2'
  | 'step3'
  | 'step3estimate'
  | 'step4'
  | 'step5'
  | 'step6overcontrib'
  | 'result';

interface WizardProps {
  onComplete?: () => void;
}

export function Wizard({ onComplete }: WizardProps = {}) {
  const { state, dispatch } = useAppState();

  const [screen, setScreen] = useState<Screen>(
    state.disclaimerAccepted ? 'welcome' : 'disclaimer'
  );

  // Wizard form state
  const [birthYear, setBirthYear] = useState<number>(1985);
  const [residencyYear, setResidencyYear] = useState<ResidencyYear>('before-2009');
  // P0-3: Residency-gap toggle. When true, the estimate auto-seed is
  // suppressed (the engine cannot model non-resident gap years in v1)
  // and an advisory directs the user to enter their CRA room manually.
  const [hasResidencyGap, setHasResidencyGap] = useState<boolean>(false);
  const [craRoom, setCraRoom] = useState<string>('');
  const [craMethod, setCraMethod] = useState<'cra' | 'estimate'>('cra');
  const [contribsYtd, setContribsYtd] = useState<string>('0');
  const [withdrawalsYtd, setWithdrawalsYtd] = useState<string>('0');
  // P0-1 wiring: ISO-8601 (YYYY-MM-DD) date the over-contribution began,
  // collected on the conditional step6overcontrib screen. Empty until set.
  const [excessStartedIso, setExcessStartedIso] = useState<string>('');
  // FIX-D: track whether the current craRoom value came from auto-seed (vs
  // user typing). When the user toggles residency-gap ON after an auto-seed,
  // we clear the field; if the user typed, we preserve it.
  const [autoSeeded, setAutoSeeded] = useState<boolean>(false);
  // FIX-D: ephemeral confirmation flag shown next to the residency-gap
  // checkbox after we clear an auto-seeded estimate.
  const [recentlyClearedEstimate, setRecentlyClearedEstimate] = useState<boolean>(false);

  function acceptDisclaimer() {
    dispatch({ type: 'ACCEPT_DISCLAIMER' });
    setScreen('welcome');
  }

  /** Local-clock YYYY-MM-DD (today). Used as the date input max bound. */
  function todayYmd(): string {
    return localIsoNow().split('T')[0]!;
  }

  function completeWizard(startedIso?: string) {
    const inputs: WizardInputs = {
      birth_year: birthYear,
      residency_year: residencyYear,
      cra_room_method: craMethod,
      cra_room_initial_cents: dollarsToCents(craRoom || '0'),
      cra_verified_at_iso: craMethod === 'cra' ? localIsoNow() : null,
      contributions_ytd_cents: dollarsToCents(contribsYtd),
      withdrawals_ytd_cents: dollarsToCents(withdrawalsYtd),
      ...(startedIso ? { prior_year_excess_started_iso: startedIso } : {}),
    };
    dispatch({ type: 'COMPLETE_WIZARD', inputs });
    setScreen('result');
  }

  /**
   * P0-1 wiring: from step 5's Calculate button, branch based on whether
   * the user's inputs produce negative available room. Negative room →
   * conditional step6overcontrib (date capture). Positive → straight to
   * result.
   */
  function finishStep5() {
    const cents = computedRoom();
    const negative = new Decimal(cents).lessThan(0);
    if (negative) {
      setScreen('step6overcontrib');
    } else {
      completeWizard();
    }
  }

  // Compute available room for result screen
  function computedRoom(): string {
    const today = localIsoNow();
    const transient = buildTFSAState(
      {
        ...state,
        wizardCompleted: true,
        wizardInputs: {
          birth_year: birthYear,
          residency_year: residencyYear,
          cra_room_method: craMethod,
          cra_room_initial_cents: dollarsToCents(craRoom || '0'),
          cra_verified_at_iso: craMethod === 'cra' ? today : null,
          contributions_ytd_cents: dollarsToCents(contribsYtd),
          withdrawals_ytd_cents: dollarsToCents(withdrawalsYtd),
        },
      },
      today
    );
    if (!transient) return '0';
    return availableRoom(transient);
  }

  // ── Screens ──────────────────────────────────────────────────────────────

  if (screen === 'disclaimer') {
    return (
      <Modal open title="Before you begin" onClose={() => {}} hideClose>
        <div class="space-y-4 text-sm text-body">
          <p>
            <strong>Hudson Bay Finance</strong> is run by Mo — a self-directed investor,{' '}
            <strong>not a CPA or financial advisor.</strong>
          </p>
          <p>
            This tool is for educational purposes only. Always verify your contribution
            room directly with CRA before making TFSA contributions.
          </p>
          <p>Your data never leaves this device. There is no server.</p>
          <ButtonPrimary onClick={acceptDisclaimer}>
            I understand — let's start
          </ButtonPrimary>
          <p class="text-center text-xs text-body/70 mt-2">
            <a
              href="/privacy"
              class="underline focus:outline-none focus:ring-2 focus:ring-hudson-navy rounded"
            >
              Privacy policy
            </a>
          </p>
        </div>
      </Modal>
    );
  }

  if (screen === 'welcome') {
    return (
      <div class="max-w-md mx-auto px-4 py-8 space-y-6">
        <h1 class="text-2xl font-bold text-hudson-navy">What we'll ask</h1>
        <ul class="space-y-3 text-sm text-body">
          {[
            'Your birth year',
            'When you became a Canadian resident',
            'Your TFSA room from CRA (or an estimate)',
            'Contributions made so far in 2026',
            'Withdrawals made so far in 2026',
          ].map((item, i) => (
            <li key={i} class="flex items-start gap-3">
              <span class="shrink-0 w-6 h-6 rounded-full bg-hudson-navy text-white text-xs flex items-center justify-center font-bold">
                {i + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p class="text-xs text-body/70">Takes about 2 minutes.</p>
        <ButtonPrimary onClick={() => setScreen('step1')} autoFocus>Get started →</ButtonPrimary>
      </div>
    );
  }

  if (screen === 'step1') {
    // FIX-C: <select> dropdown replaces <input type="number"> for birth year.
    // HTML min/max on number inputs is a soft constraint; a select enforces
    // the 1942–2008 range, eliminates typos on mobile keyboards, and is
    // better announced by screen readers. Options are ordered newest-first
    // (2008 → 1942) so the median user (50s/60s) doesn't scroll the full
    // range.
    const birthYearOptions = Array.from({ length: 67 }, (_, i) => 2008 - i);
    return (
      <WizardStep step={1} onContinue={() => setScreen('step2')}>
        <h2 class="text-xl font-bold text-hudson-navy">Your birth year</h2>
        <p class="text-sm text-body/70 mt-1">
          We use this to calculate your lifetime TFSA eligibility starting at age 18.
        </p>
        <select
          value={String(birthYear)}
          aria-label="Birth year"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onChange={(e) =>
            setBirthYear(parseInt((e.target as HTMLSelectElement).value, 10))
          }
          class="mt-4 block w-full text-xl font-bold border-b-2 border-hudson-navy bg-transparent focus:outline-none py-2"
        >
          {birthYearOptions.map((y) => (
            <option key={y} value={String(y)}>
              {y}
            </option>
          ))}
        </select>
      </WizardStep>
    );
  }

  if (screen === 'step2') {
    // FIX-D: preserve the user's path choice across back-navigation. If they
    // previously chose Estimate (craMethod==='estimate'), continue resumes
    // step3estimate rather than silently snapping them back to the CRA
    // variant. Fresh users (craMethod==='cra' by default) land on step3 CRA.
    const step3Target: Screen = craMethod === 'estimate' ? 'step3estimate' : 'step3';
    return (
      <WizardStep step={2} onBack={() => setScreen('step1')} onContinue={() => setScreen(step3Target)}>
        <h2 class="text-xl font-bold text-hudson-navy">
          When did you become a Canadian resident?
        </h2>
        <p class="text-sm text-body/70 mt-1">
          TFSA room only accumulates during years you're a resident of Canada, aged 18+.
        </p>
        <select
          value={String(residencyYear)}
          aria-label="Year became Canadian resident"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            setResidencyYear(v === 'before-2009' ? 'before-2009' : parseInt(v, 10));
          }}
          class="mt-4 block w-full text-xl font-bold border-b-2 border-hudson-navy bg-transparent focus:outline-none py-2"
        >
          <option value="before-2009">Before 2009 (born here / long-time resident)</option>
          {Array.from({ length: 17 }, (_, i) => 2009 + i).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {/* P0-3: Residency-gap toggle. When checked, estimate auto-seed
            is suppressed and the user is routed to manual CRA-room entry.
            FIX-D: when toggled ON, also clear any auto-seeded craRoom value
            so the field visibly resets — gives the user feedback that the
            checkbox did something. User-typed values are preserved. */}
        <label class="flex items-start gap-2 mt-4 text-sm text-body" htmlFor="residency-gap">
          <input
            id="residency-gap"
            type="checkbox"
            checked={hasResidencyGap}
            onChange={(e) => {
              const next = (e.target as HTMLInputElement).checked;
              setHasResidencyGap(next);
              if (next && autoSeeded) {
                setCraRoom('');
                setAutoSeeded(false);
                setRecentlyClearedEstimate(true);
              } else if (!next) {
                // POLISH-4: gap toggled OFF — clear the ephemeral "cleared"
                // confirmation so a subsequent re-toggle ON doesn't re-display
                // a stale message when nothing was actually cleared.
                setRecentlyClearedEstimate(false);
              }
            }}
            class="mt-0.5"
          />
          <span>
            Had any years outside Canada as a non-resident? (Affects your room calculation)
          </span>
        </label>
        {recentlyClearedEstimate && hasResidencyGap && (
          <p
            class="text-xs text-body/80 bg-cream border border-grain-gold rounded px-3 py-2 mt-2"
            role="status"
            aria-live="polite"
          >
            Estimate cleared — enter your CRA figure on the next step.
          </p>
        )}
      </WizardStep>
    );
  }

  if (screen === 'step3' || screen === 'step3estimate') {
    const isEstimate = screen === 'step3estimate';

    // P1-1: When the user has prior 2026 contributions, the lifetime
    // cumulative-room estimate is misleading (it doesn't subtract those
    // contributions from current available room). Suppress auto-seed and
    // require manual CRA-room entry instead.
    const hasPriorContribs =
      contribsYtd !== '' && new Decimal(contribsYtd || '0').greaterThan(0);

    const switchToEstimate = () => {
      // Auto-seed the room field with the computed lifetime room so users who
      // haven't typed anything see their correct amount (e.g. $7,000 for a
      // 2008 birth-year user) rather than $0.  Only seed when the field is
      // currently empty; if the user already typed a value, keep it.
      // P0-3: When the user has flagged a non-resident gap year, the engine
      // cannot accurately model their room — DO NOT auto-seed.
      // P1-1: When the user has prior contributions, auto-seeding the
      // lifetime room overstates available room — DO NOT auto-seed.
      if (!craRoom && !hasResidencyGap && !hasPriorContribs) {
        const estimatedCents = cumulativeRoom({
          birth_year: birthYear,
          residency_year: residencyYear,
          current_year: new Date().getFullYear(),
        });
        const estimatedDollars = new Decimal(estimatedCents).dividedBy(100).toFixed(0);
        setCraRoom(estimatedDollars);
        // FIX-D: mark this value as auto-seeded so a subsequent residency-gap
        // toggle can clear it without trampling user input.
        setAutoSeeded(true);
      }
      setCraMethod('estimate');
      setScreen('step3estimate');
    };

    return (
      <WizardStep
        step={3}
        onBack={() => setScreen('step2')}
        onContinue={() => setScreen('step4')}
      >
        <h2 class="text-xl font-bold text-hudson-navy">
          {isEstimate ? 'Estimate your available room' : 'Your TFSA room from CRA'}
        </h2>
        <p class="text-sm text-body/70 mt-1">
          {isEstimate
            ? 'Enter your best estimate. You can update this anytime from Settings.'
            : 'Log into CRA My Account to find your exact TFSA contribution room.'}
        </p>
        {/* FIX-A + POLISH-2: no-statement steer + first-time-user hint on the
            CRA variant. The hint nudges undecided users BEFORE they switch,
            which is the right side of the path-toggle. */}
        {!isEstimate && (
          <p class="text-xs text-body/70 mt-2">
            No CRA statement yet? Use the estimate option below — we'll project
            your room from your birth year. Best for first-time users.
          </p>
        )}
        {isEstimate && (
          <p class="text-xs text-body/70 bg-cream border border-grain-gold rounded px-3 py-2 mt-2 flex items-center gap-2">
            <span aria-hidden="true">⚠</span>
            <span>Estimate — verify with CRA before contributing large amounts.</span>
          </p>
        )}
        {/* P1-1: Prior-contributions advisory. The lifetime cumulative-room
            estimate doesn't account for 2026 contributions already made, so
            auto-seeding it would overstate available room. */}
        {isEstimate && hasPriorContribs && !hasResidencyGap && (
          <div class="text-xs text-body/80 bg-cream border border-alert-red rounded px-3 py-2 mt-2">
            <p>
              Enter your available room from your CRA My Account. We can't
              auto-estimate it because you have prior contributions.
            </p>
          </div>
        )}
        {/* P0-3: Residency-gap advisory. Replaces the auto-seeded estimate
            with a manual-entry directive (the engine cannot model non-resident
            gap years in v1; auto-seeding would overstate room). */}
        {isEstimate && hasResidencyGap && (
          <div class="text-xs text-body/80 bg-cream border border-alert-red rounded px-3 py-2 mt-2 space-y-2">
            <p>
              Our estimate doesn't capture non-resident gap years. Enter your TFSA
              contribution room from your CRA My Account (the 'TFSA contribution
              room' line on your most recent Notice of Assessment, updated by CRA).
            </p>
            <p>
              <a
                href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/tfsa/contributions.html"
                class="text-hudson-navy underline focus:outline-none focus:ring-2 focus:ring-hudson-navy rounded"
                target="_blank"
                rel="noopener noreferrer"
              >
                CRA TFSA contribution room
              </a>
            </p>
          </div>
        )}
        <div class="mt-4 relative">
          <span aria-hidden="true" class="absolute left-0 top-1/2 -translate-y-1/2 text-xl font-bold text-body/70">
            $
          </span>
          <input
            type="number"
            value={craRoom}
            min={0}
            step={100}
            aria-label={isEstimate ? 'Estimated TFSA room in dollars' : 'CRA TFSA room in dollars'}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onInput={(e) => {
              // P2-4: HTML min="0" is a soft constraint — browsers still
              // allow typing '-1'. Clamp non-empty negatives to '0'.
              setCraRoom(clampNonNegative((e.target as HTMLInputElement).value));
              setCraMethod(isEstimate ? 'estimate' : 'cra');
              // FIX-D: any user keystroke into the room field invalidates the
              // auto-seeded provenance — the value is now user-owned.
              setAutoSeeded(false);
            }}
            class="block w-full text-3xl font-bold pl-6 border-b-2 border-hudson-navy bg-transparent focus:outline-none py-2"
            placeholder="0"
          />
        </div>
        {/* FIX-A: promote the path-toggle to a visible secondary button so users
            with no CRA statement notice the alternative entry point. */}
        <button
          type="button"
          onClick={() => {
            if (isEstimate) {
              setCraMethod('cra');
              setScreen('step3');
            } else {
              switchToEstimate();
            }
          }}
          class="mt-4 inline-flex items-center text-sm font-medium text-hudson-navy border border-hudson-navy rounded px-3 py-2 hover:bg-cream focus:outline-none focus:ring-2 focus:ring-hudson-navy"
        >
          {isEstimate ? 'Use CRA figure instead' : 'Use estimate instead'}
        </button>
      </WizardStep>
    );
  }

  if (screen === 'step4') {
    // FIX-B: real-time advisory that fires on the linear-pass path (step3
    // Estimate → step4) the moment user types contribs > 0. The existing
    // step3 advisory (P1-1) only fires on back-navigation, which no linear
    // user reaches. craMethod is the source of truth for the path; reading
    // contribsYtd here triggers re-render on input.
    const step4PriorContribs =
      contribsYtd !== '' && new Decimal(contribsYtd || '0').greaterThan(0);
    const showEstimateAdvisory =
      craMethod === 'estimate' && step4PriorContribs && !hasResidencyGap;
    return (
      <WizardStep step={4} onBack={() => setScreen('step3')} onContinue={() => setScreen('step5')}>
        <h2 class="text-xl font-bold text-hudson-navy">2026 contributions so far</h2>
        <p class="text-sm text-body/70 mt-1">
          Total contributed to all your TFSAs in 2026. Enter 0 if none yet.
        </p>
        {showEstimateAdvisory && (
          <div
            class="text-xs text-body/80 bg-grain-gold/15 border border-grain-gold rounded px-3 py-2 mt-3"
            role="status"
          >
            <p>
              Heads up — your 2026 contributions reduce your estimate's
              accuracy. For numbers you'll act on, go back to step 3 and use
              the CRA figure from your CRA My Account.
            </p>
          </div>
        )}
        <div class="mt-4 relative">
          <span aria-hidden="true" class="absolute left-0 top-1/2 -translate-y-1/2 text-xl font-bold text-body/70">
            $
          </span>
          <input
            type="number"
            value={contribsYtd}
            min={0}
            step={100}
            aria-label="2026 contributions year-to-date in dollars"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            // P2-4: clamp non-empty negatives to '0' (HTML min="0" soft only).
            onInput={(e) =>
              setContribsYtd(clampNonNegative((e.target as HTMLInputElement).value))
            }
            class="block w-full text-3xl font-bold pl-6 border-b-2 border-hudson-navy bg-transparent focus:outline-none py-2"
          />
        </div>
      </WizardStep>
    );
  }

  if (screen === 'step5') {
    return (
      <WizardStep step={5} onBack={() => setScreen('step4')} onContinue={finishStep5}>
        <h2 class="text-xl font-bold text-hudson-navy">2026 withdrawals so far</h2>
        <p class="text-sm text-body/70 mt-1">
          Withdrawals in 2026 don't restore your room until January 1, 2027 — the TFSA bear
          trap. Enter 0 if none.
        </p>
        <div class="mt-4 relative">
          <span aria-hidden="true" class="absolute left-0 top-1/2 -translate-y-1/2 text-xl font-bold text-body/70">
            $
          </span>
          <input
            type="number"
            value={withdrawalsYtd}
            min={0}
            step={100}
            aria-label="2026 withdrawals year-to-date in dollars"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            // P2-4: clamp non-empty negatives to '0' (HTML min="0" soft only).
            onInput={(e) =>
              setWithdrawalsYtd(clampNonNegative((e.target as HTMLInputElement).value))
            }
            class="block w-full text-3xl font-bold pl-6 border-b-2 border-hudson-navy bg-transparent focus:outline-none py-2"
          />
        </div>
      </WizardStep>
    );
  }

  // P0-1 wiring: conditional over-contribution start-date capture.
  // Only reachable when finishStep5 detected negative available room.
  if (screen === 'step6overcontrib') {
    const maxDate = todayYmd();
    const dateValid =
      excessStartedIso !== '' &&
      excessStartedIso >= '2009-01-01' &&
      excessStartedIso <= maxDate;
    return (
      <WizardStep
        step={6}
        total={6}
        onBack={() => setScreen('step5')}
        onContinue={() => {
          if (!dateValid) return; // empty / out-of-range → stay put
          completeWizard(excessStartedIso);
        }}
        continueLabel="Calculate my room →"
      >
        <h2 class="text-xl font-bold text-hudson-navy">
          You're currently over your TFSA limit.
        </h2>
        <p class="text-sm text-body mt-2">
          CRA charges 1% per month on the excess until you withdraw it. To
          compute how much you owe so far, tell us when the over-contribution
          started.
        </p>
        <label
          class="block text-sm font-medium text-body mt-4"
          htmlFor="excess-started-date"
        >
          When did your contributions first exceed your room? (ISO date — see
          your CRA My Account 'Transaction history' if unsure)
        </label>
        <input
          id="excess-started-date"
          type="date"
          value={excessStartedIso}
          min="2009-01-01"
          max={maxDate}
          required
          aria-label="When did your contributions first exceed your room?"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onInput={(e) =>
            setExcessStartedIso((e.target as HTMLInputElement).value)
          }
          class="mt-2 block w-full text-xl font-bold border-b-2 border-hudson-navy bg-transparent focus:outline-none py-2"
        />
        <button
          type="button"
          onClick={() => completeWizard()}
          class="mt-3 text-sm text-hudson-navy underline focus:outline-none focus:ring-2 focus:ring-hudson-navy rounded"
        >
          I'll figure this out later
        </button>
      </WizardStep>
    );
  }

  // Result reveal (screen === 'result')
  const roomCents = computedRoom();
  const isEstimatePath = craMethod === 'estimate';
  return (
    <div class="max-w-md mx-auto px-4 py-12 text-center space-y-6">
      <p class="text-sm uppercase tracking-widest text-body/70 font-semibold">
        Your available TFSA room
      </p>
      <p class="text-5xl font-bold text-hudson-navy" data-testid="result-available-room">
        {formatCAD(roomCents)}
      </p>
      {isEstimatePath && (
        <p class="text-xs text-body/70 bg-cream border border-grain-gold rounded px-4 py-2 flex items-center gap-2">
          <span aria-hidden="true">⚠</span>
          <span>Based on your estimate — verify with CRA before contributing.</span>
        </p>
      )}
      <p class="text-sm text-body/70">
        This is your room as of today, after 2026 contributions and accounting for the bear
        trap on 2026 withdrawals.
      </p>
      <ButtonPrimary onClick={() => onComplete?.()} autoFocus>View dashboard →</ButtonPrimary>
    </div>
  );
}

// ── Wizard step shell ────────────────────────────────────────────────────────

interface WizardStepProps {
  step: number;
  onContinue: () => void;
  onBack?: () => void;
  /** P0-1 wiring: total step count for ProgressDots + aria-label. Defaults to 5. */
  total?: number;
  /** P0-1 wiring: optional override for the primary button label. */
  continueLabel?: string;
  children: import('preact').ComponentChildren;
}

function WizardStep({ step, onContinue, onBack, total = 5, continueLabel, children }: WizardStepProps) {
  // No <form> wrapper — native form submission cannot be reliably guarded
  // against Enter-key bleed between steps (browser fires submit on the new
  // form before JS guards can run). Instead, intercept Enter at the div level:
  // inputs advance on Enter (wall-clock guard prevents bleed), selects and
  // buttons are excluded (they handle Enter via their own native behavior).
  const mountedAtRef = useRef(Date.now());

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions --
          This div is a keyboard-event delegate: it captures bubbled Enter keydowns
          from its interactive children (inputs) so we can advance the step without
          a <form> wrapper (native form submit bleeds Enter keypresses between steps). */}
      <div
        role="group"
        aria-label={`Step ${step} of ${total}`}
        class="max-w-md mx-auto px-4 py-6 space-y-4"
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          const tag = (e.target as HTMLElement).tagName;
          if (tag === 'SELECT' || tag === 'BUTTON') return;
          if (Date.now() - mountedAtRef.current < 300) return;
          e.preventDefault();
          onContinue();
        }}
      >
        <div class="flex items-center gap-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back"
              class="text-hudson-navy focus:outline-none focus:ring-2 focus:ring-hudson-navy rounded p-1"
            >
              ←
            </button>
          ) : (
            <span class="w-7" aria-hidden="true" />
          )}
          <ProgressDots total={total} current={step} />
          <span class="text-xs text-body/70 ml-auto whitespace-nowrap">Step {step} of {total}</span>
        </div>
        <div class="pt-2">{children}</div>
        <div class="pt-4">
          <ButtonPrimary onClick={onContinue}>
            {continueLabel ?? (step === total ? 'Calculate my room →' : 'Continue →')}
          </ButtonPrimary>
        </div>
      </div>
    </>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function dollarsToCents(dollars: string): string {
  return new Decimal(dollars || '0').times(100).toFixed(0);
}

/**
 * P2-4: Wizard numeric inputs declare HTML min="0", but browsers still
 * accept typing/pasting a leading '-'. Clamp non-empty negatives to
 * '0' so the engine never sees an inverted CRA-room or contributions
 * value. Empty string passes through (the user is still typing).
 */
function clampNonNegative(raw: string): string {
  if (raw === '') return raw;
  const n = parseFloat(raw);
  if (Number.isFinite(n) && n < 0) return '0';
  return raw;
}
